import { formatPlayTreeKeyLabel } from './differeStatsTreeLabel';

interface TopHumanRow {
  key: string;
  card: number | null;
  count: number;
  frequency: number;
}

interface PliStatsSlice {
  entropyGrouped: number;
  distinctGrouped: number;
  top3: TopHumanRow[];
}

function pct(n: number): string {
  return `${(n * 100).toFixed(1)} %`;
}

/** Couleur : faible entropie = convergence (vert), forte = dispersion (ambre). */
function entropyBarColor(ratio: number): string {
  const t = Math.min(1, Math.max(0, ratio));
  const hue = 145 - t * 115;
  return `hsl(${hue} 62% 48%)`;
}

export function DiffereStatsEntropyCell({
  entropy,
  maxEntropy,
}: {
  entropy: number;
  maxEntropy: number;
}) {
  const ratio = entropy / Math.max(0.01, maxEntropy);
  return (
    <td className="differe-stats-entropy-cell">
      <div
        className="differe-stats-entropy-cell-inner"
        title={`Entropie : ${entropy.toFixed(2)} bit (0 = une seule carte/classe, plus haut = plus dispersé)`}
      >
        <div className="differe-stats-entropy-bar-track">
          <div
            className="differe-stats-entropy-bar-fill"
            style={{ width: `${ratio * 100}%`, background: entropyBarColor(ratio) }}
          />
        </div>
        <span className="differe-stats-entropy-num">{entropy.toFixed(2)}</span>
      </div>
    </td>
  );
}

export function DiffereStatsDistinctCell({
  distinct,
  maxDistinct,
}: {
  distinct: number;
  maxDistinct: number;
}) {
  const ratio = distinct / Math.max(1, maxDistinct);
  return (
    <td className="differe-stats-distinct-cell">
      <div
        className="differe-stats-distinct-cell-inner"
        title={`${distinct} carte${distinct > 1 ? 's' : ''} ou classe${distinct > 1 ? 's' : ''} distincte${distinct > 1 ? 's' : ''} dans le top-X`}
      >
        <div className="differe-stats-distinct-dots" aria-hidden>
          {Array.from({ length: Math.min(distinct, 8) }, (_, i) => (
            <span
              key={i}
              className="differe-stats-distinct-dot"
              style={{ opacity: 0.35 + (ratio * 0.65 * (i + 1)) / Math.min(distinct, 8) }}
            />
          ))}
        </div>
        <span className="differe-stats-distinct-num">{distinct}</span>
      </div>
    </td>
  );
}

export function DiffereStatsTopHumansCell({
  pli,
  groupEquiv,
}: {
  pli: PliStatsSlice;
  groupEquiv: boolean;
}) {
  return (
    <td className="differe-stats-top-cells">
      {pli.top3.map(row => (
        <span key={row.key} className="differe-stats-chip" title={`${row.count} joueurs`}>
          {formatPlayTreeKeyLabel(groupEquiv ? row.key : `card:${row.card}`)}
          <small>{pct(row.frequency)}</small>
        </span>
      ))}
    </td>
  );
}

export function pliColumnSpan(pliIndex: number, showTopPli1 = true): number {
  if (pliIndex !== 0) return 2;
  return showTopPli1 ? 3 : 2;
}

export function computePliColumnMaxima(
  deals: Array<{ byDepth: PliStatsSlice[] }>,
  depth: number,
): { maxEntropy: number[]; maxDistinct: number[] } {
  const maxEntropy: number[] = [];
  const maxDistinct: number[] = [];
  for (let i = 0; i < depth; i++) {
    let ent = 0.01;
    let dist = 1;
    for (const deal of deals) {
      const pli = deal.byDepth[i];
      if (!pli) continue;
      ent = Math.max(ent, pli.entropyGrouped);
      dist = Math.max(dist, pli.distinctGrouped);
    }
    maxEntropy.push(ent);
    maxDistinct.push(dist);
  }
  return { maxEntropy, maxDistinct };
}
